import React from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Loader2Icon } from "lucide-react";
import { toast } from "sonner";
import { usePayoutMethods, addPayoutMethod } from "@/_actions/userActions";
import {
  usePayableResearches,
  requestPayout,
  usePayouts,
} from "@/_actions/researchActions";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import Dinero from "dinero.js";

const schema = Yup.object().shape({
  accountHolder: Yup.string().required("Ingresa el titular de la cuenta"),
  clabe: Yup.string().required("Ingresa la CLABE"),
  bank: Yup.string().required("Ingresa el banco"),
});

export default function Comissions() {
  // getAvailable payables
  const [loading, setLoading] = React.useState(false);
  const { data: payableResearches } = usePayableResearches();
  const { data: payoutMethods, mutate: mutatePayoutMethods } =
    usePayoutMethods();
  const { data: payouts, mutate: mutatePayouts } = usePayouts();
  const navigate = useNavigate();

  const form = useForm({
    resolver: yupResolver(schema),
  });
  return (
    <div className="space-y-4">
      <section className="bg-white rounded-xl p-6">
        <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
          Comisiones pendientes de pago
        </h3>
        {payableResearches?.items.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr>
                <th>ID</th>
                <th>Monto</th>
              </tr>
            </thead>
            <tbody>
              {payableResearches?.items.map((payable) => (
                <tr key={payable.id}>
                  <td>{payable.id}</td>
                  <td>
                    {Dinero({
                      amount: payable?.referral_payment_amount,
                      currency: "MXN",
                    }).toFormat()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="my-10">
              <tr className="border-t border-gray-200 ">
                <td colSpan={2} className="text-right font-bold">
                  Subtotal
                </td>
                <td className="font-bold">
                  {Dinero({
                    amount: payableResearches?.subtotal,
                    currency: "MXN",
                  }).toFormat()}
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="text-right font-bold">
                  IVA (16%)
                </td>
                <td className="font-bold">
                  {Dinero({
                    amount: payableResearches?.tax,
                    currency: "MXN",
                  }).toFormat()}
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="text-right font-bold">
                  Total
                </td>
                <td className="font-bold">
                  {Dinero({
                    amount: payableResearches?.total,
                    currency: "MXN",
                  }).toFormat()}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : null}

        {payableResearches?.items.length === 0 && (
          <div className="w-full h-28  bg-stone-50 overflow-hidden flex items-center align-middle justify-center top-4  text-zinc-800 text-base font-normal leading-normal tracking-tight">
            Completa una investigación para generar una nueva comisión
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl p-6">
        <h3 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
          Información de pago
        </h3>
        {payoutMethods?.results?.length === 0 ? (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(async (v) => {
                await addPayoutMethod({
                  account_holder: v.accountHolder,
                  clabe_id: v.clabe,
                  bank: v.bank,
                  default: true,
                });
                mutatePayoutMethods();
              })}
              className="space-y-8"
            >
              {/* TODO: ALLOW EDIT CREATED PAYOUT ACCOUNT  */}
              <FormField
                control={form.control}
                name="accountHolder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Titular de la cuenta</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Ingresa tu nombre completo como aparece en tu banco "
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4 md:grid">
                <FormField
                  control={form.control}
                  name="clabe"
                  className="mr-4 w-full"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CLABE</FormLabel>
                      <FormControl>
                        <Input placeholder="Ingresa la CLABE" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="bank"
                  className="mr-4 w-full"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Banco</FormLabel>
                      <FormControl>
                        <Input placeholder="Ingresa el banco" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button size="lg" type="submit">
                Agregar cuenta bancaria
              </Button>
            </form>
          </Form>
        ) : (
          payoutMethods?.results.map((method) => (
            <ul key={method.id}>
              <li>{method.account_holder}</li>
              <li>{method.bank}</li>
              <li>{method.clabe_id}</li>
            </ul>
          ))
        )}
      </section>

      <div className=" space-y-2">
        <div className="justify-start text-purple-500 text-sm font-normal tracking-tight">
          {payableResearches?.items.length === 0
            ? "No hay Comisiones por cobrar.*"
            : "Completa la información para solicitar un pago.*"}
        </div>
        <Button
          onClick={() => {
            setLoading(true);

            const data = {
              membership_payout_data: payoutMethods.results[0]?.id,
              items_requested: payableResearches?.items.map((item) => item.id),
              base: payableResearches.subtotal,
              tax: payableResearches.tax,
              total: payableResearches.total,
            };
            requestPayout(data)
              .then((response) => {
                toast.success("Pago solicitado con éxito");
                navigate(`/investigaciones/comisiones/${response.id}`);
              })
              .catch((error) => {
                toast.error("Error al solicitar el pago");
              })
              .finally(() => {
                setLoading(false);
              });
          }}
          disabled={
            payoutMethods?.results.length === 0 ||
            payableResearches?.items.length === 0 ||
            loading
          }
          size="lg"
          type="button"
        >
          {loading && <Loader2Icon className="animate-spin" />}
          Solicitar pago
        </Button>
      </div>

      {payouts?.results?.length > 0 && (
        <section className="bg-white rounded-xl p-6 space-y-3">
          <h2 className="text-zinc-800 text-xl font-bold leading-relaxed tracking-tight">
            Historial de pagos
          </h2>
          {/* list with link to payout detail */}
          <ul>
            {payouts.results.map((payout) => (
              <li key={payout.id}>
                <Link
                  className="text-blue-500 hover:underline"
                  to={`/investigaciones/comisiones/${payout.id}`}
                >
                  {new Dinero({
                    amount: payout.total,
                    currency: "MXN",
                  }).toFormat()}{" "}
                  - {payout.id} - {payout.status}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
